







































































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

/**
 * Utils
 */
import classes from "@/config/wow/classes";
import * as externalLinks from "@/config/external-links";
import * as imageLinks from "@/config/image-links";
import armorTypes from "@/config/wow/armor-types";
import itemQualities from "@/config/wow/item-qualities";
import difficulties from "@/config/wow/difficulties";

import { getElegantDateString, getLocaleStringFromDateString, getTimeSince } from "@/utils/date-helpers";
import { sortByAsc, sortByDesc } from "@/utils/sort-helpers";


/**
 * Components
 */
import CharacterCell from "@/components/_global/CharacterCell.vue";
import WishesByItemTable from "@/components/dashboard/components/wishes/WishesByItemTable.vue";
import DataFetcher from "@/components/_global/DataFetcher.vue";

/**
 * API
 */
import teamsAPI from "@/api/teams";
import raidsAPI from "@/api/raids";
import { GetTeamRaidWishesResponse } from "@/api/teams/get-team-raid-wishes.response";
import { GetRaidsResponse } from "@/api/raids/get-raids.response";
import { GetOneTeamResponse } from "@/api/teams/get-one-team.response";
import { GetRostersResponse } from "@/api/rosters/get-rosters.response";
import { Guid } from "@/domain/guid";

/**
 * Types
 */

/**
 * VueX Modules
 */
const teamModule = namespace("team");

const wishValueToQualityColor = {
	30: "legendary",
	20: "rare",
	15: "uncommon",
	10: "basic"
};


/**
 * Rosters wishes page.
 */
@Component({
	components: {
		DataFetcher,
		WishesByItemTable,
		CharacterCell
	}
})
export default class WishesPage extends Vue {
	/// Injections
	classes = classes;
	externalLinks = externalLinks;
	imageLinks = imageLinks;
	armorTypes = armorTypes;
	difficulties = difficulties;
	itemQualities = itemQualities;
	getTimeSince = getTimeSince;
	getElegantDateString = getElegantDateString;
	getLocaleStringFromDateString = getLocaleStringFromDateString;


	wishValueToQualityColor = wishValueToQualityColor;
	/// Props
	@Prop()
	rosters!: GetRostersResponse.Roster[];

	/// Attributes
	isLoading = false;

	raids: GetRaidsResponse.Raid[] | null = null;
	wishes: GetTeamRaidWishesResponse | null = null;

	activeRaidID: string | null = null;
	activeBossID: string | null = null;

	viewMode = 0;

	sortByValue = null;

	showEmptyRows = false;

	get viewPerItem() {
		return this.viewMode == 1;
	}

	/// VueX Modules
	@teamModule.State("team")
	currentTeam!: GetOneTeamResponse;

	get sortByItems() {
		return [{
			text: "Class",
			value: "class",
		}, {
			text: "Updated",
			value: "updated",
		}];
	}

	get headers() {
		if (this.activeRaid == null) {
			return [];
		}
		return [
			{
				text: this.$t("composition.character"),
				value: "character",
				align: "left",
				class: "collapsing",
				sortable: false,
				divider: true
			},
			...this.displayedBosses
				.map(boss =>
					({
						text: boss.name,
						value: boss.id,
						align: "left",
						sortable: false,
						class: !this.activeBossID ? "collapsing" : "",
					})
				),
			{
				text: "Updated",
				value: "lastLootWishesUpdateTime",
				align: "left",
				class: "collapsing",
				sortable: false
			},
		];
	}

	get isTableFixedColumns() {
		return this.$vuetify.breakpoint.mdAndUp && !this.activeBossID;
	}

	get displayedBosses(): GetRaidsResponse.Boss[] {
		if (this.activeRaid == null) {
			return [];
		}
		return this.activeRaid.bosses
			.filter(boss =>
				this.activeBossID ? boss.id == this.activeBossID : true
			);
	}

	get activeRaidBosses(): GetRaidsResponse.Boss[] | null {
		if (this.raids == null) {
			return null;
		}

		const activeRaid = this.raids?.find((x) => x.id === this.activeRaidID) ?? null;
		if (activeRaid == null) {
			return activeRaid;
		}
		return activeRaid.bosses;
	}

	get displayedData() {
		if (this.displayedRoster == null) {
			return [];
		}
		const activeRaid = this.activeRaid;
		if (activeRaid == null) {
			return [];
		}

		const wishes = this.wishes;
		if (wishes == null) {
			return [];
		}


		const result = this.displayedRoster
			.characters
			.map((character) => {
				const res: any = {};
				const characterWishes = wishes[character.id];

				let hasAtLeastOneLoot = false;
				for (const boss of activeRaid.bosses) {
					res[boss.id] = characterWishes?.wishList[boss.id]?.sort(sortByDesc(x => x.value));
					hasAtLeastOneLoot = hasAtLeastOneLoot || (res[boss.id]?.length > 0);
				}
				res.lastLootWishesUpdateTime = characterWishes?.lastLootWishesUpdateTime ?? null;
				res.character = character;
				return res;
			});

		let sortedByNameResult = [...result].sort(sortByAsc(x => x.character.name));

		if (this.sortByValue == "class") {
			sortedByNameResult = sortedByNameResult.sort(sortByAsc(x => x.character.class));
		}
		else if (this.sortByValue == "updated") {
			sortedByNameResult = sortedByNameResult.sort(sortByAsc(x => x.lastLootWishesUpdateTime != null ? x.lastLootWishesUpdateTime : ""));
		}
		return sortedByNameResult;
	}

	/**
	 * The current roster to display.
	 */
	get displayedRoster(): GetRostersResponse.Roster | null {
		return this.rosters?.find(x => x.id == this.$route.params.rosterId) ?? null;
	}

	get activeRaid(): GetRaidsResponse.Raid | null {
		return this.raids?.find(x => x.id === this.activeRaidID) ?? null;
	}

	@Watch("activeRaidID")
	async onActiveRaidIDChanged() {
		if (this.activeRaidID) {
			this.activeBossID = null;
			this.wishes = await teamsAPI.getRaidWishes(this.currentTeam.id, this.activeRaidID);
		}
	}

	/// Methods
	async created() {
	}

	async fetch() {
		this.raids = await raidsAPI.fetchRaids();
		if (this.raids && this.raids.length > 0) {
			this.activeRaidID = this.raids[0].id;
		}
	}

	getSlotName(bossID: Guid) {
		return `item.${bossID}`;
	}

	getHeaderSlotName(bossID: Guid) {
		return `header.${bossID}`;
	}

	handleToggleShowOnlyUsedItems() {
		this.showEmptyRows = !this.showEmptyRows;
	}
}
